import isEmpty from 'lodash/isEmpty';
import { increamentDays, getMonth, formatNumber } from './formattingUtils';
import { LOBS_CONSTANTS, PAGE_NAME, WEEKDAYS } from '../constants/packageConstants';
import transferUtils from './transferUtils';
import { getMMT_HLD_OBJ, getServerIP } from './appUtils';
import { CARD_COLLAPSIBLE_STATE } from '../../dynamicDetails_v3/src/constants';

const destMapping = {};
let dayWiseImages = {};
const destinationsMapping = {};
const dates = {};
const getCityName = (code, destinations) => {
  const city = destinations.filter(destination => destination.cityCode === code);
  return city.length > 0 ? city[0].name : false;
};
const getCityCountryName = (code, destinations) => {
  const city = destinations.filter(destination => destination.cityCode === code)[0];
  return city
    ? city.name === city.countryName
      ? city.name
      : `${city.name}, ${city.countryName}`
    : false;
};
const getDateByIndex = (index, packageDates) => {
  if (packageDates && packageDates.length) {
    const firstDate = packageDates[index < 0 ? 0 : index];
    if (firstDate instanceof Date) {
      return `${formatNumber(firstDate.getDate())} ${getMonth(firstDate.getMonth() + 1)}, ${
        WEEKDAYS[firstDate.getDay()]
      }`;
    }
    return '';
  }
};
export const getDate = (date, isReview = false) => {
  const formatDate = getISTTime(new Date(date));
  if (isReview) {
    return `${WEEKDAYS[formatDate.getDay()]}, ${formatNumber(formatDate.getDate())} ${getMonth(
      formatDate.getMonth() + 1
    )}`;
  }
  return `${formatNumber(formatDate.getDate())} ${getMonth(formatDate.getMonth() + 1)}, ${
    WEEKDAYS[formatDate.getDay()]
  }`;
};
const getActivityFreebies = packageInfo =>
  packageInfo && packageInfo.freebiesDetails && packageInfo.freebiesDetails.activityFreebies;

const getPackageDayItineraries = (packageInfo, pageName = '') => {
  let dataSet = 'dayItineraries';
  let dayItineraries = [];
  if (pageName === PAGE_NAME.DETAILS_V3) {
    if (
      packageInfo?.itineraryDetail?.dynamicItinerary?.dayItinerariesV2 ||
      packageInfo?.itineraryDetail?.dayItinerariesV2
    ) {
      dataSet = 'dayItinerariesV2';
    } else {
      dataSet = 'dayItineraries';
    }
  } else if (pageName === PAGE_NAME.REVIEW_V3) {
    dataSet = 'dayItinerariesV2';
  }
  // dataSet = 'dayItinerariesV2';
  if (packageInfo?.itineraryDetail?.dynamicItinerary?.[dataSet]) {
    dayItineraries = packageInfo.itineraryDetail.dynamicItinerary[dataSet];
  } else if (packageInfo?.itineraryDetail?.[dataSet]) {
    dayItineraries = packageInfo?.itineraryDetail?.[dataSet];
  }
  return dayItineraries;
};

export const getPackageActivities = packageInfo => {
  let activities = [];
  if (packageInfo?.activityDetail?.cityActivities) {
    activities = packageInfo.activityDetail.cityActivities
      .reduce(
        (previous, current) =>
          previous.concat(
            current.activities.map(activity => {
              activity.staySequence = current.staySequence;
              return activity;
            })
          ),
        []
      )
      .sort((a, b) => (a.day - b.day < 0 ? -1 : 1));
  }

  return activities;
};

export const checkForOverNightFlights = packageInfo => {
  if (packageInfo?.flightDetail) {
    const flights = getPackageFlights(packageInfo);
    return flights.filter(flight => flight.isOvernight);
  }
  return [];
};
export const getPackageFlights = packageInfo => {
  let flights = [];
  if (packageInfo && packageInfo.flightDetail && !packageInfo.flightDetail.removed) {
    const { flightDetail } = packageInfo;
    if (flightDetail.flightDetailType === 'OBT') {
      if (flightDetail.obtFlightGroup && flightDetail.obtFlightGroup.flights) {
        flights = flights.concat(flightDetail.obtFlightGroup.flights);
      }
    } else if (flightDetail.flightDetailType === 'DOM_RETURN') {
      if (flightDetail.departureFlight) {
        flights.push(flightDetail.departureFlight);
      }
      if (flightDetail.returnFlight) {
        flights.push(flightDetail.returnFlight);
      }
    } else if (flightDetail.flightDetailType === 'DOM_ONWARDS') {
      if (flightDetail.flightGroup) {
        flights = flightDetail.flightGroup.flights.map(flight => ({
          ...flight,
          api_version: 'v2',
        }));
      } else if (flightDetail.onwardFlights) {
        flights = flightDetail.onwardFlights.map(flight => ({
          ...flight,
          api_version: 'v1',
        }));
      }
    }
  }

  return flights;
};

export const getPackageHotels = packageInfo => {
  let hotels = [];
  if (packageInfo?.hotelDetail?.hotelCategoryDetails) {
    hotels = packageInfo.hotelDetail.hotelCategoryDetails
      .reduce((previous, current) => previous.concat(current.hotels), [])
      .filter(hotel => !hotel.removed);
  } else if (packageInfo?.hotelDetail?.hotels) {
    hotels = packageInfo?.hotelDetail?.hotels;
  }
  return hotels;
};

export const getPackageTransfers = packageInfo => {
  const transfers = {
    airportTransfers: [],
    carTransfers: [],
    cityDropTransfers: [],
  };
  if (packageInfo?.transferDetail?.airportTransfers && !packageInfo?.transferDetail?.removed) {
    transfers.airportTransfers = packageInfo.transferDetail.airportTransfers;
    transfers.airportTransfers = transfers.airportTransfers
      .filter(transfer => transfer.defaultSelection !== 'NONE')
      .map(transfer => {
        if (transfer.defaultSelection === 'PRIVATE') {
          return {
            ...transfer.privateTransfer,
            cityId: transfer.cityId,
            defaultSelection: transfer.defaultSelection,
            transferSequence: transfer.transferSequence,
            staySequence: transfer?.staySequence || '',
            transferAthType: transfer?.type || '',
          };
        }
        if (transfer.defaultSelection === 'SHARED') {
          return {
            ...transfer.groupTransfer,
            cityId: transfer.cityId,
            defaultSelection: transfer.defaultSelection,
            transferSequence: transfer.transferSequence,
            staySequence: transfer?.staySequence || '',
            transferAthType: transfer?.type || '',
          };
        }
        return {};
      });
  }

  if (packageInfo?.carItineraryDetail?.carItineraries) {
    transfers.carTransfers = packageInfo.carItineraryDetail.carItineraries.filter(
      itinerary => !itinerary.removed
    );
    transfers.carTransfers = transfers.carTransfers
      .map(carTransfer => {
        carTransfer.commuteDetails.forEach(commuteDetail => {
          commuteDetail.day = carTransfer.startDay;
        });
        return carTransfer.commuteDetails;
      })
      .reduce((acc, current) => (acc = acc.concat(current)), []);
  }

  if (packageInfo?.cityDropDetail?.cityDrops) {
    transfers.cityDropTransfers = packageInfo.cityDropDetail.cityDrops;
    transfers.cityDropTransfers = transfers.cityDropTransfers
      .map(cityDrop => {
        cityDrop.commuteDetails.forEach(commuteDetail => {
          commuteDetail.day = cityDrop.startDay;
        });
        return cityDrop.commuteDetails;
      })
      .reduce((acc, current) => (acc = acc.concat(current)), []);
  }

  return transfers;
};

const getPackageDestinations = packageInfo => {
  const key = packageInfo?.destinationDetail?.destinations?.[0]?.start;
  if (!key) return [];
  if (Array.isArray(destinationsMapping[key])) {
    return destinationsMapping[key];
  }
  if (packageInfo?.destinationDetail?.destinations) {
    destinationsMapping[key] = packageInfo.destinationDetail.destinations.map(destination => ({
      ...destination,
      start:
        typeof destination.start === 'string'
          ? destination.start
          : formatMilliSecToYYYYMMDDStr(destination.start),
      end:
        typeof destination.start === 'string'
          ? destination.end
          : formatMilliSecToYYYYMMDDStr(destination.end),
    }));
  }

  return destinationsMapping[key];
};

const checkForExpiredPage = response => {
  if (
    !isEmpty(response.data.error) &&
    (response.data.error.code === 'PACKAGE012' ||
      response.data.error.code === 'REVIEW013' ||
      response.data.error.code === 'REVIEW014')
  ) {
    return true;
  }
  return false;
};

export const getISTTime = date => {
  const currentTime = new Date(date);
  const currentOffset = currentTime.getTimezoneOffset();
  const ISTOffset = 330; // IST offset UTC +5:30
  const ISTTime = new Date(currentTime.getTime() + (ISTOffset + currentOffset) * 60000);

  return ISTTime;
};

export const getPackageDates = (packageInfo, isReview = false) => {
  const key = packageInfo?.destinationDetail?.destinations?.[0]?.start;
  if (!key) return [];
  if (Array.isArray(dates[key])) {
    return dates[key];
  }
  dates[key] = [];
  if (packageInfo) {
    // In YYYY-MM-DD format
    const destinations = getPackageDestinations(packageInfo);
    let firstDestination = {};
    if (isReview) {
      firstDestination = destinations[0];
    } else {
      firstDestination = destinations.sort((a, b) =>
        a.staySequence - b.staySequence < 0 ? -1 : 1
      )[0];
    }
    const startDateStr = firstDestination && firstDestination.start;
    if (startDateStr) {
      const [year, month, date] = startDateStr.split('-');
      const dateObj = getISTTime(new Date(Number(year), Number(month) - 1, Number(date)));
      dates[key].push(dateObj);
      const dayItineraries =
        packageInfo?.itineraryDetail?.dayItineraries || getPackageDayItineraries(packageInfo);
      for (let count = 1; count < dayItineraries.length; count++) {
        dates[key].push(increamentDays(dateObj, count));
      }
    }
  }
  return dates[key];
};

const getMonthDateDayMap = packageDates => {
  const monthDateDayMap = {};
  const count = 1;
  packageDates.forEach(date => {
    const month = getMonth(date.getMonth() + 1);
    if (!monthDateDayMap[month]) {
      monthDateDayMap[month] = [];
    }
    monthDateDayMap[month].push([date.getDate(), WEEKDAYS[date.getDay()]]);
  });

  return monthDateDayMap;
};

const getTransferBySellableId = (sellableId, transfers) => {
  const { airportTransfers, carTransfers, cityDropTransfers } = transfers;
  if (airportTransfers) {
    for (let i = 0; i < airportTransfers.length; i++) {
      if (airportTransfers[i].sellableId === sellableId) {
        return { ...airportTransfers[i], type: 'AIRPORT_TRANSFER' };
      }
    }
  }

  if (carTransfers) {
    for (let i = 0; i < carTransfers.length; i++) {
      if (carTransfers[i].sellableId === sellableId) {
        return { ...carTransfers[i], type: 'CAR_ITINERARY' };
      }
    }
  }

  if (cityDropTransfers) {
    for (let i = 0; i < cityDropTransfers.length; i++) {
      if (cityDropTransfers[i].sellableId === sellableId) {
        return cityDropTransfers[i];
      }
    }
  }
};
export const getDayWiseFlightDetails = (flights, itineraryUnit) => {
  const selectedFlightSellableId = itineraryUnit?.flight?.sellableId;
  if (!selectedFlightSellableId) return {};
  const selectedFlight = flights.filter(
    flight => flight.sellableId === selectedFlightSellableId
  )[0];
  if (!selectedFlight) return {};
  return selectedFlight;
};

export const getDayWiseHotelDetails = (hotels, itineraryUnit) => {
  const hotelSellableId = itineraryUnit.hotel && itineraryUnit.hotel.hotelSellableId;
  if (!hotelSellableId && itineraryUnit.text) {
    return getMMT_HLD_OBJ()?.acmeV3
      ? {
          text: itineraryUnit?.address,
          heading: `HOTEL ${itineraryUnit?.itineraryUnitSubType}`,
        }
      : { text: itineraryUnit.text };
  }
  if (!hotelSellableId) {
    return {};
  }
  const selectedHotel = hotels.filter(hotel => hotel.sellableId === hotelSellableId)[0];
  if (!selectedHotel) return {};
  return selectedHotel;
};

const getDayWiseMealDetails = itineraryUnit => ({ text: itineraryUnit.text });

const getTransferImagesList = selectedTransfer => {
  if (selectedTransfer?.vehicleInfo && selectedTransfer?.vehicleInfo?.imageUrl) {
    return [selectedTransfer?.vehicleInfo?.imageUrl];
  }
  if (selectedTransfer?.imageDetail?.images) {
    const imageArray = [];
    selectedTransfer?.imageDetail?.images.map(image => {
      imageArray.push(image?.path);
    });
    return imageArray;
  }
  return [];
};

export const getDayWiseTransferDetails = (transfers, itineraryUnit, day) => {
  const sellableId = itineraryUnit?.car?.sellableId;
  if (!sellableId && itineraryUnit?.text) {
    return { text: itineraryUnit?.text };
  }
  if (!sellableId) {
    return {};
  }
  const selectedTransfer = getTransferBySellableId(sellableId, transfers);
  if (!selectedTransfer) return {};
  let inclusions =
    selectedTransfer?.additionalInfo?.inclusions ||
    selectedTransfer?.additionalData?.inclusions ||
    [];
  let exclusions =
    selectedTransfer?.additionalInfo?.exclusions ||
    selectedTransfer?.additionalData?.exclusions ||
    [];
  const inclusionText =
    selectedTransfer?.additionalData?.inclusionText || itineraryUnit?.car?.inclusionText;

  inclusions = typeof inclusions === 'string' ? inclusions.split('~') : inclusions;
  exclusions = typeof exclusions === 'string' ? exclusions.split('~') : exclusions;
  const transfer = {
    type: selectedTransfer?.type,
    transferAthType: selectedTransfer?.transferAthType,
    transferSequence: selectedTransfer?.transferSequence,
    day,
    defaultSelection: selectedTransfer?.defaultSelection,
    transferType: transferUtils.getTransferType(selectedTransfer),
    heading: itineraryUnit?.shortText || itineraryUnit?.text,
    description:
      selectedTransfer?.additionalInfo?.description ||
      selectedTransfer?.additionalData?.description,
    inclusions,
    exclusions,
    importantInfos: selectedTransfer?.additionalInfo?.importantInfos,
    shortText: itineraryUnit?.shortText,
    isLandOnly: itineraryUnit?.isLandOnly,
    landOnlyDescription: itineraryUnit?.landOnlyDescription,
    driverSpokenLanguages: selectedTransfer?.vehicleInfo?.driverSpokenLanguages,
    facilities: selectedTransfer?.vehicleInfo?.facilities,
    imageUrls: getTransferImagesList(selectedTransfer),
    model: selectedTransfer?.vehicleInfo?.model,
    sellableId: selectedTransfer?.sellableId,
    vehicleName: selectedTransfer?.vehicleInfo?.vehicleName,
    vehicleMaxPax: selectedTransfer?.vehicleInfo?.maxPaxCapacity,
    vehicleCategory: selectedTransfer?.vehicleInfo?.vehicleCategory,
    duration: selectedTransfer?.metaData?.duration,
    transferCode: selectedTransfer?.metaData?.code,
    startDay: selectedTransfer?.day,
    isLocked: selectedTransfer?.metaData?.locked,
    inclusionText,
    startingPoint:
      selectedTransfer?.additionalInfo?.pickUpPoint ||
      selectedTransfer?.additionalData?.pickUpPoint,
    endingPoint:
      selectedTransfer?.additionalInfo?.dropOffPoint ||
      selectedTransfer?.additionalData?.dropOffPoint,
    suitableForPaxType: selectedTransfer?.metaData?.suitableForPaxType,
    safe: selectedTransfer?.safe || selectedTransfer?.additionalData?.safe,
    safetyRating: selectedTransfer?.safetyRating || selectedTransfer?.additionalData?.safetyRating,
    commuteCode: selectedTransfer?.commuteCode,
    metaData: selectedTransfer?.metaData,
    recheckKey: selectedTransfer?.recheckKey,
    staySequence: selectedTransfer?.staySequence,
    unavailable: selectedTransfer?.unavailable,
    activityTourGrades: selectedTransfer?.activityTourGrades,
  };
  return transfer;
};

export const getDayWiseActivityDetails = (
  activities,
  itineraryUnit,
  destinations,
  activityFreebies
) => {
  const activityFreebiesMap =
    activityFreebies && new Map(activityFreebies.map(freebie => [freebie.code, freebie]));
  const sellableId = itineraryUnit.activity && itineraryUnit.activity.sellableId;
  if (!sellableId && itineraryUnit.text) {
    return { text: itineraryUnit.text };
  }
  if (!sellableId) {
    return {};
  }
  let selectedActivity = activities.filter(activity => activity.sellableId === sellableId)[0];
  if (!selectedActivity) return {};

  selectedActivity = {
    ...selectedActivity,
    heading: `Activity in ${getCityName(selectedActivity.metaData.cityCode, destinations)}`,
    cityCountryName: getCityCountryName(selectedActivity.metaData.cityCode, destinations),
    isFreebie:
      activityFreebiesMap && activityFreebiesMap.has(selectedActivity.sellableId.split('_')[0]),
  };

  return selectedActivity;
};
const getPackageMedia = packageInfo => {
  let videos = [];
  if (packageInfo?.videoDetail?.packageVideo?.mpdUrl) {
    const videoMpdUrl = packageInfo.videoDetail.packageVideo.mpdUrl;
    videos = typeof videoMpdUrl === 'string' ? [videoMpdUrl] : videoMpdUrl;
  } else if (packageInfo?.metadataDetail?.videoUrl) {
    const { videoUrl } = packageInfo.metadataDetail;
    videos = typeof videoUrl === 'string' ? [videoUrl] : videoUrl;
  }

  return {
    images: packageInfo?.imageDetail?.images || [],
    videos,
    gallery: packageInfo?.imageDetail?.gallery || [],
    imageGalleryDetails: packageInfo?.imageDetail?.gallerySections || [],
  };
};
const getCarSightSeeings = carContents => {
  if (carContents && carContents.length) {
    let itineraries = [];
    carContents.forEach(itinerary => {
      itineraries = itineraries.concat(
        itinerary.carDayWiseContents.map(dayWiseSightseeings => ({
          day: dayWiseSightseeings.dayNumber,
          sellableId: itinerary.sellableId,
          itineraryUnitType: 'SIGHTSEEING',
          itineraryUnitSubType: 'CAR_SIGHTSEEING',
          carSightseeings: dayWiseSightseeings.carSightseeingDetails,
          locationImageUrl: dayWiseSightseeings.locationImageUrl,
          carExtraInfo: dayWiseSightseeings?.carExtraInfo,
          cardCollapsibleState:
            dayWiseSightseeings?.cardCollapsibleState ||
            CARD_COLLAPSIBLE_STATE.COLLAPSIBLE_EXPANDED,
        }))
      );
    });
    return itineraries;
  }
  return [];
};

const getFormattedDurationText = nightCount => {
  if (!nightCount) return '';
  return `${nightCount}N ${nightCount + 1}D`;
};

const getDestinationByDay = (day, destinations) => {
  if (day === 0) {
    return {
      name: '',
    };
  }
  if (destMapping[`day${day}`]) {
    return destinations[destMapping[`day${day}`]];
  }
  for (let i = 0; i < destinations.length; i++) {
    for (let j = destinations[i].startDay; j <= destinations[i].endDay; j++) {
      destMapping[`day${j}`] = i;
    }
  }
  return destinations[destMapping[`day${day}`]];
};
const getDayWiseImage = images => {
  if (!images) {
    return {};
  }
  if (Object.keys(dayWiseImages).length !== 0) {
    return dayWiseImages;
  }
  dayWiseImages = images.reduce((image, x) => {
    if (!image[x.day]) {
      image[x.day] = [];
    }
    image[x.day].push(x);
    return image;
  }, {});
  return dayWiseImages;
};
const getIncludedTransferCount = dayItineraries => {
  if (dayItineraries.length !== 0) {
    let count = 0;
    dayItineraries.forEach(dayItinerary => {
      dayItinerary?.itineraryUnits?.forEach(itineraryUnit => {
        if (itineraryUnit?.itineraryUnitType === LOBS_CONSTANTS.TRANSFER) {
          count += 1;
        } else if (itineraryUnit?.itineraryUnitType === LOBS_CONSTANTS.COMMUTE) {
          itineraryUnit?.commute?.forEach(itineraryUnitCommute => {
            if (
              itineraryUnitCommute?.itineraryUnitType === LOBS_CONSTANTS.TRANSFER &&
              !itineraryUnitCommute?.isRemoved
            ) {
              count += 1;
            }
          });
        } else if (
          itineraryUnit?.itineraryUnitType === LOBS_CONSTANTS.ACTIVITY &&
          itineraryUnit.itineraryUnitSubType === LOBS_CONSTANTS.TRANSFER_OTHER &&
          getMMT_HLD_OBJ().acmeV3
        ) {
          count += 1;
        }
      });
    });
    return count;
  }
};
const getPackageTagDestName = packageInfo => {
  let packageTagDestName = '';
  if (packageInfo?.tagDestination?.name) {
    packageTagDestName = packageInfo.tagDestination.name;
  }
  return packageTagDestName;
};
const getTrackerParams = params => {
  const { departureDate, fromCity, toCity, branch } = params;
  const trackerParams = {
    departureCity: fromCity,
    departureDate,
    destinationCity: toCity,
    branch,
    serverIp: getServerIP(),
  };
  return trackerParams;
};
const getTagDestinationsWithDay = (day, destinations, dayWiseData) => {
  const destinationArray = [];
  dayWiseData.forEach(lobItem => {
    const { cityId } = lobItem;
    if (destinationArray.findIndex(dest => dest.cityId === cityId) === -1) {
      const foundDestination = destinations.find(dest => dest.cityId === cityId);
      if (foundDestination) destinationArray.push(foundDestination);
    }
  });
  return destinationArray;
};

export const getPaxCount = rooms => {
  let childrenCount = 0;
  let adultCount = 0;
  rooms?.forEach?.(room => {
    childrenCount += room?.noOfChildrenWB + room?.noOfChildrenWOB;
    adultCount += room?.noOfAdults + room?.noOfInfants;
  });

  let returnString = `${adultCount} ${adultCount === 1 ? 'Adult' : 'Adults'}`;

  if (childrenCount) {
    returnString += `, ${childrenCount} ${childrenCount === 1 ? 'Child' : 'Children'}`;
  }

  return returnString;
};

export default {
  getActivityFreebies,
  getPackageDayItineraries,
  getPackageActivities,
  getPackageFlights,
  getPackageHotels,
  getPackageTransfers,
  getPackageDestinations,
  checkForExpiredPage,
  getPackageDates,
  getMonthDateDayMap,
  getDayWiseFlightDetails,
  getDayWiseHotelDetails,
  getDayWiseTransferDetails,
  getDayWiseActivityDetails,
  getDateByIndex,
  getDate,
  getPackageMedia,
  getCarSightSeeings,
  getFormattedDurationText,
  getDestinationByDay,
  getDayWiseImage,
  getIncludedTransferCount,
  getPackageTagDestName,
  getTrackerParams,
  getDayWiseMealDetails,
  getTagDestinationsWithDay,
  checkForOverNightFlights,
};
